import { signOut as amplifySignOut } from 'aws-amplify/auth';

import { clearAuthStorage } from './utils/tokenCache';

/**
 * How long server-side revocation may hold up a sign-out. Healthy networks
 * answer in a few hundred milliseconds; this only bounds a hung request.
 */
export const REVOKE_BUDGET_MS = 2000;

/**
 * Sign the user out of THIS device: revoke the session, then wipe it from disk.
 *
 * The one sign-out every path must use. Before this, four places signed users
 * out by resetting Redux alone — the Sign Out buttons in the side nav and in
 * Settings, the post-delete-account exit, and the invalid-token guard — and all
 * four left the Cognito tokens in localStorage, so a reload silently signed the
 * user back in (BUG-19).
 *
 * ORDER MATTERS, and it is not "clear first":
 *
 * 1. Amplify's `signOut()` reads the stored tokens, REVOKES the refresh token
 *    server-side for this device, then clears its own store. Wiping storage
 *    before it runs would leave nothing to revoke — and revocation is what
 *    protects a refresh token that was copied off this machine before sign-out.
 *    (Not a global sign-out: the user's other devices stay signed in.)
 * 2. The wipe runs in `finally`, so it happens however step 1 ends — success, a
 *    thrown error, or a request that never answers. A sign-out that leaves
 *    tokens behind because the network was slow is the bug, not an edge case.
 *
 * Step 1 is raced against `REVOKE_BUDGET_MS` so a hung request delays the wipe by
 * at most that long rather than indefinitely.
 */
export async function signOutAndClearStorage(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      amplifySignOut(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, REVOKE_BUDGET_MS);
      }),
    ]);
  } catch (error) {
    console.warn('Sign-out revocation failed; clearing local session anyway.', error);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    clearAuthStorage();
  }
}
